import { useEffect, useRef, useState } from "react";
import { Smartphone } from "lucide-react";

import { ProcessInstantLinkPrototype } from "@/components/landing/InstantQrPrototypePanel";
import { useI18n } from "@/lib/i18n";

const processSteps = [
  {
    key: "link",
    label: "Link Min to WhatsApp",
  },
  {
    key: "voice",
    label: "Send or receive voice notes",
  },
  {
    key: "summary",
    label: "Min sends the summary as text",
  },
];

export default function InboundMagic({
  prototypeAwakeSignal = 0,
  prototypeEnabled = false,
  prototypePolling = false,
  prototypeQrReady = false,
  prototypeRecovering = false,
  prototypeQrImageSrc = "",
  prototypePairingCode = "",
  prototypeState = "qr",
  onCanonicalLink,
  onPrototypeActivate,
  onPrototypeConfigure,
  onPrototypeOpenMin,
  onPrototypeOpenWhatsApp,
  onPrototypeRequestCode,
  onPrototypeStateChange,
}) {
  const { tx } = useI18n();
  const sectionRef = useRef(null);
  const [revealedItems, setRevealedItems] = useState(() => new Set());
  const [scrollRevealEnabled] = useState(() => (
    typeof window !== "undefined"
    && "IntersectionObserver" in window
    && !window.matchMedia("(prefers-reduced-motion: reduce)").matches
  ));
  const [hasStartedScrolling, setHasStartedScrolling] = useState(false);
  const prototypeQrPending = !prototypeQrReady && !["linked", "configured"].includes(prototypeState);

  useEffect(() => {
    if (!scrollRevealEnabled || hasStartedScrolling) {
      return undefined;
    }

    const handleScroll = () => {
      if (window.scrollY > 0) setHasStartedScrolling(true);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasStartedScrolling, scrollRevealEnabled]);

  useEffect(() => {
    if (!scrollRevealEnabled || !hasStartedScrolling || !sectionRef.current) {
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      setRevealedItems((currentItems) => {
        const nextItems = new Set(currentItems);
        let changed = false;
        entries.forEach((entry) => {
          if (entry.isIntersecting && !nextItems.has(entry.target.dataset.processReveal)) {
            nextItems.add(entry.target.dataset.processReveal);
            changed = true;
          }
        });
        return changed ? nextItems : currentItems;
      });
    }, { rootMargin: "0px 0px -26% 0px", threshold: 0.25 });

    const revealItems = sectionRef.current.querySelectorAll("[data-process-reveal]");
    revealItems.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, [hasStartedScrolling, scrollRevealEnabled]);

  return (
    <section
      ref={sectionRef}
      className={`landing-process${scrollRevealEnabled ? " landing-process--scroll-reveal" : ""}`}
      aria-labelledby="landing-process-title"
    >
      <div className="landing-shell">
        <div className="landing-process__heading">
          <h2 id="landing-process-title">{tx("Min tells you what's important.")}</h2>
          <div className="landing-process__tags" aria-label={tx("Benefits")}>
            {["Save time", "Get the bullet points", "Catch up faster", "No audio needed", "not just a transcription"].map((label, index) => {
              const revealKey = index === 2 || index === 4 ? "intro-benefits" : `tag-${index}`;
              return (
                <span
                  key={label}
                  data-process-reveal={revealKey}
                  className={revealedItems.has(revealKey) ? "is-revealed" : undefined}
                >
                  {tx(label)}
                </span>
              );
            })}
          </div>
        </div>

        <ol className="landing-process__steps">
          {processSteps.map((step, index) => (
            <li
              key={step.key}
              data-process-reveal={step.key}
              className={`landing-process__step landing-process__step--${step.key}${revealedItems.has(step.key) ? " is-revealed" : ""}`}
            >
              {step.key === "link" ? (
                prototypeEnabled ? (
                  <div className={`landing-link-demo landing-link-demo--prototype${prototypeQrPending ? " is-qr-pending" : ""}`}>
                    <ProcessInstantLinkPrototype
                      awakeSignal={prototypeAwakeSignal}
                      polling={prototypePolling}
                      qrImageSrc={prototypeQrImageSrc}
                      qrReady={prototypeQrReady}
                      recovering={prototypeRecovering}
                      pairingCode={prototypePairingCode || undefined}
                      state={prototypeState}
                      onActivate={onPrototypeActivate}
                      onStateChange={onPrototypeStateChange}
                      onConfigure={onPrototypeConfigure}
                      onOpenMin={onPrototypeOpenMin}
                      onOpenWhatsApp={onPrototypeOpenWhatsApp}
                      onRequestCode={onPrototypeRequestCode}
                    />
                  </div>
                ) : (
                  <button
                    type="button"
                    className="landing-link-demo"
                    aria-label={tx("Link Min to WhatsApp")}
                    onClick={onCanonicalLink}
                  >
                    <Smartphone aria-hidden="true" />
                    <span className="landing-link-demo__label">{tx("Your WhatsApp number")}</span>
                    <div className="landing-link-demo__number" aria-hidden="true">
                      <span>+27</span>
                      <span>82 000 0001</span>
                    </div>
                    <span className="landing-link-demo__action">{tx("Get code")}</span>
                  </button>
                )
              ) : null}

              {step.key === "voice" ? (
                <div className="landing-voice-demo" aria-label={tx("Voice note, 4 minutes 52 seconds")}>
                  <img
                    src="/landing/voice-note-demo.png"
                    alt=""
                    width="630"
                    height="157"
                  />
                </div>
              ) : null}

              {step.key === "summary" ? (
                <div className="landing-summary-demo">
                  <img className="landing-summary-demo__mark" src="/landing/min-mark.png" alt="" aria-hidden="true" />
                  <p>{tx("Summary: Max is going for a run, then meeting friends for breakfast. Reply if you can join.")}</p>
                </div>
              ) : null}

              <div className="landing-process__step-label">
                <span aria-hidden="true">{index + 1}</span>
                <p>{tx(step.label)}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
